'use client';

/**
 * ObeyaCostPlanGraphCard — full-bleed CostPlan graph (Reagraph).
 *
 * Same wrapping pattern as ObeyaGraphCard (project graph): the id comes
 * from `useViewportSelection().mcpId` — deliberately NOT `entityId`,
 * which also tracks `selectedNode` and would get hijacked the moment
 * the user clicks an RFQ/vendor/room node inside the graph itself
 * (selectNode() only sets selectedNode, never mcpId).
 *
 * mcpId/mcpName resolve tab-first (the active tab's own entityId/title),
 * store-second (graph-selection-store's mcpId/mcpName, set by McpRow's
 * explorer click via setMcpContext). Tab wins because switching tabs via
 * the tab strip (setActiveTab) never touches the store — if the store
 * won, re-visiting an earlier cost-plan tab after opening a different
 * one would still show the more-recently-clicked plan. No RAG legend
 * overlay here — a cost plan has no health/status dimension the way a
 * project does; CostPlanGraphReagraph's own type-color legend is enough.
 *
 * The scene export lives entirely in CostPlanGraphReagraph now (it's the
 * one with `data` — see its own comment), NOT here. This wrapper used to
 * also call useCardExport with a bare {mcp_id} placeholder, which meant
 * a selected plan produced TWO scene entries at the same slot — one real,
 * one a stale stand-in. IdleCostPlanGraph below is a separate component
 * (not a conditional hook call — rules of hooks) so exactly one of
 * {idle export, real export} is ever mounted, matching the doctrine that
 * scene.surfaces mirrors mount points 1:1 (card-export.tsx).
 *
 * @see klyde_edge/ui_config/composition/cards/obeya.py — content_key="obeya_costplan_graph"
 * @see klyde_ipe/components/canvas/cards/obeya/graph-card.tsx — the project-graph sibling
 *
 * @component_role: Card
 * @component_type: Card Renderer
 * @domain: Canvas / Obeya
 * @layer: presentation
 */

import { useMemo } from 'react';
import { CostPlanGraphReagraph } from './costplan-graph-reagraph';
import { useCardExport } from '@/lib/design/card-export';
import { useCardIdentity } from '@/lib/design/card-chrome';
import { useViewportSelection } from '@/lib/stores/viewport-selection';

export function ObeyaCostPlanGraphCard() {
  const { mcpId, mcpName } = useViewportSelection();

  if (!mcpId) {
    return <IdleCostPlanGraph />;
  }

  return (
    <div className="relative h-full w-full overflow-hidden">
      <CostPlanGraphReagraph costPlanId={mcpId} costPlanName={mcpName ?? undefined} />
    </div>
  );
}

/** Registers the scene export only while nothing is selected — the
 *  "loaded" export takes over once CostPlanGraphReagraph mounts, and
 *  this one unmounts (cleanup removes it from the registry), so the two
 *  never coexist. */
function IdleCostPlanGraph() {
  const { contentKey } = useCardIdentity();
  useCardExport(useMemo(() => ({
    content_key: contentKey ?? 'obeya_costplan_graph',
    status: 'idle' as const,
    query_key: null,
    text: 'Cost plan graph',
    json: { mcp_id: null },
  }), [contentKey]));

  return (
    <div className="flex items-center justify-center h-full text-xs text-muted-foreground">
      Select a cost plan
    </div>
  );
}
